import { Commit, Dispatch } from 'vuex';
import { isApiError } from '@/types/api';
import { ScheduledRun, ScheduledRunResult, NewScheduledRunPayload, UpdateScheduledRunPayload } from '@/types/schedules';
import {
  getScheduledRuns,
  createScheduledRun,
  updateScheduledRun,
  deleteScheduledRun,
  toggleScheduledRun,
  getScheduledRunResults
} from '@/utils/api';

export interface ScheduledRunsState {
  runs: Record<string, ScheduledRun>;
  results: Record<string, { items: ScheduledRunResult[], total: number }>;
}

export const scheduledRuns = () => ({
  state: <ScheduledRunsState>{
    runs: {},
    results: {},
  },
  mutations: {
    setScheduledRuns(state: ScheduledRunsState, runs: ScheduledRun[]) {
      state.runs = runs.reduce((acc, run) => {
        acc[run.id] = run;
        return acc;
      }, {} as Record<string, ScheduledRun>);
    },
    setScheduledRun(state: ScheduledRunsState, run: ScheduledRun) {
      state.runs[run.id] = run;
    },
    deleteScheduledRun(state: ScheduledRunsState, runId: string) {
      delete state.runs[runId];
    },
    setRunResults(state: ScheduledRunsState, { runId, results, total }: { runId: string, results: ScheduledRunResult[], total: number }) {
      state.results[runId] = { items: results, total };
    },
  },
  actions: {
    async fetchScheduledRuns({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      const response = await getScheduledRuns();
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Error fetching schedules: ${response.message}`, color: 'error' }, { root: true });
        return;
      }
      commit('setScheduledRuns', response);
    },
    async createScheduledRun({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, payload: NewScheduledRunPayload) {
      const response = await createScheduledRun(payload);
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Error creating schedule: ${response.message}`, color: 'error' }, { root: true });
        return null;
      }
      commit('setScheduledRun', response);
      dispatch('updateSnackbar', { text: 'Schedule created successfully.' }, { root: true });
      return response;
    },
    async updateScheduledRun({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, { id, payload }: { id: string, payload: UpdateScheduledRunPayload }) {
      const response = await updateScheduledRun(id, payload);
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Error updating schedule: ${response.message}`, color: 'error' }, { root: true });
        return null;
      }
      commit('setScheduledRun', response);
      dispatch('updateSnackbar', { text: 'Schedule updated successfully.' }, { root: true });
      return response;
    },
    async deleteScheduledRun({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, id: string) {
      const response = await deleteScheduledRun(id);
      if (isApiError(response) || (response && 'success' in response && !response.success)) {
        const message = isApiError(response) ? response.message : 'An unknown error occurred.';
        dispatch('updateSnackbar', { text: `Error deleting schedule: ${message}`, color: 'error' }, { root: true });
        return;
      }
      commit('deleteScheduledRun', id);
      dispatch('updateSnackbar', { text: 'Schedule deleted successfully.' }, { root: true });
    },
    async toggleScheduledRun({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, id: string) {
      const response = await toggleScheduledRun(id);
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Error toggling schedule: ${response.message}`, color: 'error' }, { root: true });
        return;
      }
      commit('setScheduledRun', response);
      dispatch('updateSnackbar', { text: `Schedule status updated to '${response.status}'.` }, { root: true });
    },
    async fetchRunResults({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, { runId, limit, offset }: { runId: string, limit?: number, offset?: number }) {
      const response = await getScheduledRunResults(runId, limit, offset);
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Error fetching results: ${response.message}`, color: 'error' }, { root: true });
        return;
      }
      commit('setRunResults', { runId, results: response.results, total: response.total });
    }
  },
  getters: {
    scheduledRunsList: (state: ScheduledRunsState) => Object.values(state.runs).sort((a, b) => b.createdAt - a.createdAt),
    getScheduledRunById: (state: ScheduledRunsState) => (id: string) => state.runs[id],
    getResultsByRunId: (state: ScheduledRunsState) => (runId: string) => state.results[runId],
  }
});
